<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    title="Exportar Contactos por CSV"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="min-h-[300px] max-h-[60vh] overflow-y-auto px-1 py-1 no-scrollbar">
      <div v-if="currentStep === 1" class="space-y-4">
        <!-- Secciones de filtrado separadas por líneas sutiles, sin recuadro -->
        <div class="divide-y divide-slate-200 dark:divide-slate-700">
          <!-- Atributos personalizados -->
          <section class="py-4 first:pt-0">
            <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              Atributos
            </p>

            <div v-if="isLoadingAttributes" class="flex items-center gap-3 text-slate-600 dark:text-slate-300">
              <Spinner />
              <span class="text-sm">Cargando atributos personalizados...</span>
            </div>
            <div v-else-if="availableCustomAttributes.length === 0" class="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Icon icon="i-lucide-info" class="w-4 h-4 shrink-0" />
              <span class="text-sm">No hay atributos personalizados definidos para contactos.</span>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="(filter, index) in customAttributeFilters"
                :key="filter.id"
                class="flex items-center gap-3"
              >
                <div class="flex-1 min-w-0">
                  <Select
                    v-model="filter.attribute"
                    :options="availableCustomAttributes"
                    option-value="uid"
                    option-label="name"
                    placeholder="Seleccionar atributo..."
                    @change="onAttributeChange(index)"
                  />
                </div>

                <div v-if="filter.attribute" class="flex-1 min-w-0">
                  <SearchableValueSelect
                    v-model="filter.value"
                    :options="valueOptions(filter)"
                    :loading="filter.valuesLoading"
                    :server-search="isServerSearched(filter.attribute)"
                    placeholder="Seleccionar valor..."
                    @search="term => onValueSearch(filter, term)"
                  />
                </div>

                <Button
                  v-if="customAttributeFilters.length > 1 || filter.attribute"
                  icon="i-lucide-x"
                  size="sm"
                  variant="ghost"
                  color="ruby"
                  class="self-center shrink-0"
                  @click="removeFilter(index)"
                />
              </div>

              <Button
                icon="i-lucide-plus"
                variant="ghost"
                size="sm"
                @click="addFilter"
              >
                Agregar filtro
              </Button>
            </div>
          </section>

          <!-- Agente propietario de la conversación -->
          <section class="py-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              Agente propietario <span class="normal-case font-normal text-slate-400 dark:text-slate-500">· opcional</span>
            </p>

            <div class="flex items-center gap-3">
              <div class="flex-1 min-w-0">
                <Select
                  v-model="selectedAgentId"
                  :options="agents"
                  option-value="id"
                  option-label="name"
                  placeholder="Todos los agentes"
                />
              </div>
              <Button
                v-if="selectedAgentId"
                icon="i-lucide-x"
                variant="ghost"
                color="ruby"
                size="sm"
                class="self-center shrink-0"
                @click="selectedAgentId = ''"
              />
            </div>
          </section>

          <!-- Rangos de fecha (última actividad + creación) -->
          <section class="py-4 space-y-4">
            <div>
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Última actividad <span class="normal-case font-normal text-slate-400 dark:text-slate-500">· opcional</span>
                </p>
                <button
                  v-if="dateFilter.from || dateFilter.to"
                  type="button"
                  class="text-xs text-ruby-600 dark:text-ruby-400 hover:underline"
                  @click="clearDateFilter"
                >
                  Limpiar
                </button>
              </div>
              <div class="flex items-center gap-3">
                <input
                  v-model="dateFilter.from"
                  type="date"
                  :max="dateFilter.to || undefined"
                  aria-label="Última actividad desde"
                  class="flex-1 py-2.5 pl-3 pr-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-woot-500 focus:border-transparent outline-none"
                />
                <span class="text-slate-400 dark:text-slate-500 text-sm">→</span>
                <input
                  v-model="dateFilter.to"
                  type="date"
                  :min="dateFilter.from || undefined"
                  aria-label="Última actividad hasta"
                  class="flex-1 py-2.5 pl-3 pr-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-woot-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div>
              <div class="flex items-center justify-between mb-2">
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Fecha de creación <span class="normal-case font-normal text-slate-400 dark:text-slate-500">· opcional</span>
                </p>
                <button
                  v-if="creationDateFilter.from || creationDateFilter.to"
                  type="button"
                  class="text-xs text-ruby-600 dark:text-ruby-400 hover:underline"
                  @click="clearCreationDateFilter"
                >
                  Limpiar
                </button>
              </div>
              <div class="flex items-center gap-3">
                <input
                  v-model="creationDateFilter.from"
                  type="date"
                  :max="creationDateFilter.to || undefined"
                  aria-label="Fecha de creación desde"
                  class="flex-1 py-2.5 pl-3 pr-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-woot-500 focus:border-transparent outline-none"
                />
                <span class="text-slate-400 dark:text-slate-500 text-sm">→</span>
                <input
                  v-model="creationDateFilter.to"
                  type="date"
                  :min="creationDateFilter.from || undefined"
                  aria-label="Fecha de creación hasta"
                  class="flex-1 py-2.5 pl-3 pr-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-woot-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <div v-if="currentStep === 2" class="space-y-4">
        <!-- Paso 2: Configurar orden de columnas -->
        <div class="divide-y divide-slate-200 dark:divide-slate-700">
          <!-- Orden de columnas -->
          <section class="py-4 first:pt-0">
            <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              Orden de columnas para el CSV
            </p>

            <div class="space-y-3">
              <div
                v-for="(column, index) in orderedColumns"
                :key="`column-${index}`"
                class="flex items-center gap-3"
              >
                <!-- Número de orden -->
                <div class="flex-shrink-0 w-8 h-8 bg-woot-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  {{ index + 1 }}
                </div>

                <!-- Selector de columna o nombre fijo -->
                <div class="flex-1 min-w-0">
                  <Select
                    v-if="!column.required"
                    v-model="column.key"
                    :options="getAvailableColumnsForIndex(index)"
                    option-value="key"
                    option-label="name"
                    placeholder="Seleccionar columna..."
                    @change="updateColumnName(index)"
                  />

                  <div v-else class="px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
                    <span class="font-semibold text-slate-900 dark:text-white">{{ column.name }}</span>
                    <span class="text-xs text-slate-400 dark:text-slate-500 ml-2">(obligatorio)</span>
                  </div>
                </div>

                <!-- Botón eliminar -->
                <Button
                  v-if="!column.required && orderedColumns.length > 2"
                  icon="i-lucide-x"
                  size="sm"
                  variant="ghost"
                  color="ruby"
                  class="self-center shrink-0"
                  @click="removeColumn(index)"
                />
              </div>

              <!-- Botón agregar columna -->
              <Button
                v-if="canAddMoreColumns"
                icon="i-lucide-plus"
                variant="ghost"
                size="sm"
                @click="addColumn"
              >
                Agregar columna
              </Button>
            </div>
          </section>

          <!-- Opciones de exportación -->
          <section class="py-4">
            <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
              Opciones <span class="normal-case font-normal text-slate-400 dark:text-slate-500">· opcional</span>
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div class="flex items-start gap-3">
                <Switch v-model="useVariableNames" class="mt-0.5" />
                <div>
                  <p class="text-sm font-medium text-slate-900 dark:text-white">
                    Nombrar columnas extra como variables
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Las columnas adicionales usan variable_1, variable_2… en vez del título del atributo.
                  </p>  
                </div>
              </div>

              <div class="flex items-start gap-3">
                <Switch v-model="excludePhoneless" class="mt-0.5" />
                <div>
                  <p class="text-sm font-medium text-slate-900 dark:text-white">
                    Excluir contactos sin teléfono
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Deja fuera del CSV los contactos sin número, requerido para envíos masivos.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- Previsualización -->
          <section class="py-4">
            <div class="flex items-center justify-between mb-3">
              <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Previsualización del CSV
              </p>
              <span class="text-xs text-slate-400 dark:text-slate-500">{{ validColumnsCount }} columnas</span>
            </div>
            <div class="font-mono text-sm text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
              {{ csvPreview }}
            </div>
          </section>
        </div>
      </div>

      <div v-if="currentStep === 3" class="space-y-6">
        <!-- Paso 3: Generación y descarga -->
        <div class="text-center">
          <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-6 text-left">
            Generar y descargar CSV
          </p>

          <div v-if="isGenerating" class="space-y-4">
            <Spinner class="mx-auto" />
            <div>
              <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                <div
                  class="bg-woot-600 h-3 rounded-full transition-all duration-300"
                  :style="{ width: `${exportProgress}%` }"
                />
              </div>
              <p class="text-sm text-slate-600 dark:text-slate-400 mt-3">
                Procesando {{ processedContacts }}/{{ totalContactsToExport }} contactos... ({{ exportProgress }}%)
              </p>
            </div>
          </div>

          <div v-else-if="exportCompleted" class="space-y-6">
            <div class="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-full flex items-center justify-center">
              <Icon icon="i-lucide-check" class="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <p class="text-slate-900 dark:text-white font-medium">¡CSV generado exitosamente!</p>
            <Button @click="downloadCsv">
              <Icon icon="i-lucide-download" class="mr-2" />
              Descargar CSV
            </Button>
          </div>

          <div v-else>
            <div class="space-y-4">
              <div class="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-left">
                <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">Resumen de exportación</p>
                <ul class="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                  <li class="flex items-center gap-2">
                    <Icon icon="i-lucide-users" class="w-4 h-4 text-woot-600 dark:text-woot-400" />
                    Contactos a exportar: <span class="font-semibold text-slate-900 dark:text-white">{{ totalContactsToExport }}</span>
                  </li>
                  <li class="flex items-center gap-2">
                    <Icon icon="i-lucide-columns" class="w-4 h-4 text-woot-600 dark:text-woot-400" />
                    Columnas incluidas: <span class="font-semibold text-slate-900 dark:text-white">{{ validColumnsCount }}</span>
                  </li>
                  <li class="flex items-center gap-2">
                    <Icon icon="i-lucide-filter" class="w-4 h-4 text-woot-600 dark:text-woot-400" />
                    Filtros aplicados: <span class="font-semibold text-slate-900 dark:text-white">{{ activeFiltersCount }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="totalContactsToExport > 5000" class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div class="flex items-center gap-2">
                  <Icon icon="i-lucide-alert-triangle" class="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <p class="text-sm text-amber-800 dark:text-amber-200 font-medium">
                    Gran cantidad de contactos detectada. La exportación puede tardar unos minutos.
                  </p>
                </div>
              </div>

              <Button
                :disabled="validColumnsCount === 0 || totalContactsToExport === 0"
                @click="generateCsv"
              >
                {{ totalContactsToExport > 5000 ? 'Generar CSV (puede tardar)' : 'Generar CSV' }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between items-center w-full gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <Button
            v-if="currentStep > 1"
            variant="faded"
            color="slate"
            label="Anterior"
            @click="previousStep"
          />
          <div
            v-if="filteredContactsCount !== null && currentStep < 3"
            class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
          >
            <Icon icon="i-lucide-users" class="w-4 h-4 text-woot-600 dark:text-woot-400 shrink-0" />
            <span>
              Contactos encontrados: <span class="font-semibold text-slate-900 dark:text-white">{{ filteredContactsCount }}</span>
            </span>
          </div>
        </div>
        <div class="flex gap-3 shrink-0">
          <Button
            variant="faded"
            color="slate"
            label="Cancelar"
            @click="closeDialog"
          />
          <Button
            v-if="currentStep < 3"
            :disabled="!canProceedToNextStep"
            label="Siguiente"
            @click="nextStep"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Select from 'dashboard/components/crm/Select.vue';
import SearchableValueSelect from 'dashboard/components/crm/SearchableValueSelect.vue';
import ContactCsvExportService from 'dashboard/services/ContactCsvExportService';

const emit = defineEmits(['export']);

// Referencias y estado
const dialogRef = ref(null);
const currentStep = ref(1);
const isGenerating = ref(false);
const exportCompleted = ref(false);
const exportProgress = ref(0);
const processedContacts = ref(0);
const csvData = ref(null);

// Crea un filtro de atributo vacío (id único para el :key del v-for)
const createEmptyFilter = () => ({
  id: Date.now() + Math.random(),
  attribute: '',
  value: '',
  valueOptions: [],
  valuesLoading: false,
});

const customAttributeFilters = ref([createEmptyFilter()]);

// Excluye contactos sin teléfono del export (útil para envíos masivos)
const excludePhoneless = ref(false);

// Filtro por rango de fecha de última actividad del contacto (ambos extremos opcionales)
const dateFilter = ref({ from: '', to: '' });

// Filtro por rango de fecha de creación del contacto (independiente del anterior;
// si se completan ambos filtros, se combinan con AND en el backend)
const creationDateFilter = ref({ from: '', to: '' });

// Si está activo, las columnas extra (no obligatorias) se exportan con headers
// variable_1, variable_2, ... en vez del título del atributo. Los envíos masivos
// usan estos nombres para identificar a qué variable apunta cada dato.
const useVariableNames = ref(false);

// Filtro por agente propietario (assignee) de la conversación
const agents = ref([]);
const selectedAgentId = ref('');

// Custom attributes disponibles
const availableCustomAttributes = ref([]);
const filteredContactsCount = ref(null);
const isLoadingAttributes = ref(false);

// Columnas disponibles y seleccionadas
const availableColumns = ref([]);

// Columnas ordenadas para el CSV (empezamos con las obligatorias).
// Los headers de las obligatorias van en plural ("Telefonos"/"Nombres")
// porque los envíos masivos los usan como nombres de columna.
const orderedColumns = ref([
  { key: 'phone_number', name: 'Telefonos', type: 'standard', required: true },
  { key: 'name', name: 'Nombres', type: 'standard', required: true }
]);

// Claves que pueden repetirse como columnas extra aunque ya vayan como
// obligatorias (nombre/teléfono): permite volcar ese dato en una variable.
const REPEATABLE_KEYS = ['name', 'phone_number'];

// Columnas base del sistema
const systemColumns = [
  { key: 'name', name: 'Nombre', type: 'standard' },
  { key: 'phone_number', name: 'Número', type: 'standard' },
  { key: 'email', name: 'Email', type: 'standard' },
  { key: 'created_at', name: 'Fecha de creación', type: 'standard' },
  { key: 'last_activity_at', name: 'Última actividad', type: 'standard' }
];

// Computed properties
const totalContactsToExport = computed(() => filteredContactsCount.value || 0);
const activeFiltersCount = computed(() => {
  const attributeFilters = customAttributeFilters.value.filter(f => f.attribute && f.value).length;
  const hasDateFilter = dateFilter.value.from || dateFilter.value.to ? 1 : 0;
  const hasCreationDateFilter = creationDateFilter.value.from || creationDateFilter.value.to ? 1 : 0;
  const hasAgentFilter = selectedAgentId.value ? 1 : 0;
  const hasPhonelessFilter = excludePhoneless.value ? 1 : 0;
  return attributeFilters + hasDateFilter + hasCreationDateFilter + hasAgentFilter + hasPhonelessFilter;
});

// Construye el payload de un rango de fecha (null si ambos extremos vacíos)
const buildRange = (range) => {
  const { from, to } = range;
  if (!from && !to) return null;
  return { from: from || null, to: to || null };
};
const buildDateRange = () => buildRange(dateFilter.value);
const buildCreationDateRange = () => buildRange(creationDateFilter.value);

const canProceedToNextStep = computed(() => {
  if (currentStep.value === 1) {
    // Solo avanzar si hay al menos un contacto que coincide con los filtros
    return filteredContactsCount.value !== null && filteredContactsCount.value > 0;
  }
  if (currentStep.value === 2) {
    return validColumnsCount.value >= 2; // Al menos las 2 obligatorias
  }
  return true;
});

const validColumnsCount = computed(() => {
  return orderedColumns.value.filter(col => col.key && col.key !== '').length;
});

const validColumns = computed(() =>
  orderedColumns.value.filter(col => col.key && col.key !== '')
);

const validAttributeFilters = computed(() =>
  customAttributeFilters.value
    .filter(f => f.attribute && f.value)
    .map(f => {
      const attrDef = attributeDef(f.attribute);
      return { attribute: attrDef?.key || f.attribute, value: f.value, model: attrDef?.model || 'contact_attribute' };
    })
);

// Headers finales del CSV, en paralelo a validColumns. Las obligatorias usan su
// nombre; las extra usan variable_N (en orden) si el toggle está activo, o el
// título del atributo si no.
const resolvedHeaders = computed(() => {
  let variableIndex = 0;
  return validColumns.value.map(col => {
    if (col.required) return col.name;
    if (useVariableNames.value) {
      variableIndex += 1;
      return `variable_${variableIndex}`;
    }
    return col.name || col.key;
  });
});

const csvPreview = computed(() => resolvedHeaders.value.join(' | '));

const canAddMoreColumns = computed(() => {
  // name/phone se pueden repetir, así que si están disponibles siempre hay algo por agregar
  if (availableColumns.value.some(col => REPEATABLE_KEYS.includes(col.key))) return true;
  const usedKeys = orderedColumns.value.map(col => col.key).filter(key => key);
  return usedKeys.length < availableColumns.value.length;
});

const getAvailableColumnsForIndex = computed(() => {
  return (index) => {
    // Los repetibles no cuentan como "usados": pueden elegirse en varias columnas
    const usedKeys = orderedColumns.value
      .map((col, i) => i !== index ? col.key : null)
      .filter(key => key && !REPEATABLE_KEYS.includes(key));

    return availableColumns.value.filter(
      col => REPEATABLE_KEYS.includes(col.key) || !usedKeys.includes(col.key)
    );
  };
});

// Métodos
const loadAgents = async () => {
  try {
    const response = await ContactCsvExportService.getAgents();
    agents.value = (response.data || []).map(agent => ({ id: agent.id, name: agent.name }));
  } catch (error) {
    console.error('Error loading agents:', error);
    agents.value = [];
  }
};

const loadCustomAttributes = async () => {
  isLoadingAttributes.value = true;
  try {
    const [contactRes, convRes] = await Promise.all([
      ContactCsvExportService.getContactAttributeDefinitions(),
      ContactCsvExportService.getConversationAttributeDefinitions(),
    ]);

    const contactAttrs = (contactRes.data || []).map(attr => ({
      uid: `contact_attribute:${attr.attribute_key}`,
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
      values: attr.attribute_values || [],
      model: 'contact_attribute',
    }));

    const convAttrs = (convRes.data || []).map(attr => ({
      uid: `conversation_attribute:${attr.attribute_key}`,
      key: attr.attribute_key,
      name: `${attr.attribute_display_name} (Conv.)`,
      type: attr.attribute_display_type,
      values: attr.attribute_values || [],
      model: 'conversation_attribute',
    }));

    availableCustomAttributes.value = [...contactAttrs, ...convAttrs];

    // Columnas del CSV solo con atributos de contacto
    const customColumns = contactAttrs.map(attr => ({
      key: attr.key,
      name: attr.name,
      type: 'custom',
    }));

    availableColumns.value = [...systemColumns, ...customColumns];
  } catch (error) {
    console.error('Error loading custom attributes:', error);
    availableCustomAttributes.value = [];
    availableColumns.value = [...systemColumns];
    useAlert('Error al cargar atributos personalizados');
  } finally {
    isLoadingAttributes.value = false;
  }
};

const attributeDef = (uid) => availableCustomAttributes.value.find(a => a.uid === uid);

// Los tipos de valor libre traen valores desde el backend (con búsqueda);
// list/checkbox usan opciones locales de la definición.
const isServerSearched = (key) => {
  const type = attributeDef(key)?.type;
  return !['list', 'checkbox'].includes(type);
};

const valueOptions = (filter) =>
  (filter.valueOptions || []).map(v => ({ id: v, name: v }));

const loadFilterValues = async (filter, search = '') => {
  const def = attributeDef(filter.attribute);
  if (!def) {
    filter.valueOptions = [];
    return;
  }

  if (def.type === 'list') {
    filter.valueOptions = (def.values || []).map(String);
    return;
  }
  if (def.type === 'checkbox') {
    filter.valueOptions = ['true', 'false'];
    return;
  }

  filter.valuesLoading = true;
  try {
    filter.valueOptions = await ContactCsvExportService.fetchAttributeValues({
      attributeKey: def.key,
      model: def.model,
      search,
    });
  } catch (error) {
    console.error('Error loading attribute values:', error);
    filter.valueOptions = [];
  } finally {
    filter.valuesLoading = false;
  }
};

const onAttributeChange = (index) => {
  const filter = customAttributeFilters.value[index];
  if (!filter) return;
  filter.value = '';
  filter.valueOptions = [];
  loadFilterValues(filter);
  updateFilteredContactsCount();
};

const searchTimers = new WeakMap();
const onValueSearch = (filter, search) => {
  if (!isServerSearched(filter.attribute)) return;
  clearTimeout(searchTimers.get(filter));
  searchTimers.set(filter, setTimeout(() => loadFilterValues(filter, search), 300));
};

const addFilter = () => {
  customAttributeFilters.value.push(createEmptyFilter());
};

const removeFilter = (index) => {
  if (customAttributeFilters.value.length > 1) {
    customAttributeFilters.value.splice(index, 1);
  } else {
    // Único filtro: lo reseteo en vez de dejar la lista vacía
    customAttributeFilters.value.splice(index, 1, createEmptyFilter());
  }
};

const clearDateFilter = () => {
  dateFilter.value = { from: '', to: '' };
};

const clearCreationDateFilter = () => {
  creationDateFilter.value = { from: '', to: '' };
};

const updateFilteredContactsCount = async () => {
  try {
    const count = await ContactCsvExportService.getFilteredContactsCount(
      validAttributeFilters.value,
      buildDateRange(),
      buildCreationDateRange(),
      selectedAgentId.value || null,
      excludePhoneless.value
    );
    filteredContactsCount.value = count;
  } catch (error) {
    console.error('Error getting filtered contacts count:', error);
    filteredContactsCount.value = 0;
  }
};

// Métodos para manejar columnas ordenadas
const addColumn = () => {
  orderedColumns.value.push({
    key: '',
    name: '',
    type: 'custom',
    required: false
  });
};

const removeColumn = (index) => {
  if (!orderedColumns.value[index].required) {
    orderedColumns.value.splice(index, 1);
  }
};

const updateColumnName = (index) => {
  const column = orderedColumns.value[index];
  if (column.key) {
    const foundColumn = availableColumns.value.find(col => col.key === column.key);
    if (foundColumn) {
      column.name = foundColumn.name;
      column.type = foundColumn.type;
    }
  } else {
    column.name = '';
    column.type = 'custom';
  }
};

const nextStep = () => {
  if (currentStep.value < 3) {
    currentStep.value++;
    if (currentStep.value === 3) {
      updateFilteredContactsCount();
    }
  }
};

const previousStep = () => {
  currentStep.value--;
};

const generateCsv = async () => {
  isGenerating.value = true;
  exportProgress.value = 0;
  processedContacts.value = 0;

  try {
    const columnOrder = validColumns.value.map(col => col.key);
    const columnHeaders = resolvedHeaders.value;

    // Usar el servicio para generar CSV con progress tracking
    const result = await ContactCsvExportService.generateCsv({
      filters: validAttributeFilters.value,
      columnOrder,
      columnHeaders,
      dateRange: buildDateRange(),
      creationDateRange: buildCreationDateRange(),
      agentId: selectedAgentId.value || null,
      excludePhoneless: excludePhoneless.value,
      onProgress: ({ processed, total, percentage }) => {
        processedContacts.value = processed;
        exportProgress.value = percentage;
      }
    });

    csvData.value = result.csvData;
    exportCompleted.value = true;
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    useAlert('Error al generar el CSV');
  } finally {
    isGenerating.value = false;
  }
};

const downloadCsv = () => {
  if (!csvData.value) return;

  // Prepend UTF-8 BOM so Excel/Sheets open accented characters correctly
  const blob = new Blob(['\uFEFF' + csvData.value], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `contactos_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  useAlert('CSV descargado exitosamente');
  closeDialog();
};

const closeDialog = () => {
  dialogRef.value?.close();
  resetForm();
};

const resetForm = () => {
  currentStep.value = 1;
  isGenerating.value = false;
  exportCompleted.value = false;
  exportProgress.value = 0;
  processedContacts.value = 0;
  csvData.value = null;
  customAttributeFilters.value = [createEmptyFilter()];
  dateFilter.value = { from: '', to: '' };
  creationDateFilter.value = { from: '', to: '' };
  selectedAgentId.value = '';
  excludePhoneless.value = true;
  filteredContactsCount.value = null;
  
  // Resetear columnas ordenadas
  orderedColumns.value = [
    { key: 'phone_number', name: 'Telefonos', type: 'standard', required: true },
    { key: 'name', name: 'Nombres', type: 'standard', required: true }
  ];
  useVariableNames.value = false;
};

// Watchers
watch(customAttributeFilters, () => {
  updateFilteredContactsCount();
}, { deep: true, immediate: false });

watch(dateFilter, () => {
  updateFilteredContactsCount();
}, { deep: true, immediate: false });

watch(creationDateFilter, () => {
  updateFilteredContactsCount();
}, { deep: true, immediate: false });

watch(selectedAgentId, () => {
  updateFilteredContactsCount();
});

watch(excludePhoneless, () => {
  updateFilteredContactsCount();
});

// Lifecycle
onMounted(async () => {
  try {
    await Promise.all([loadCustomAttributes(), loadAgents()]);
    await updateFilteredContactsCount();
  } catch (error) {
    console.error('Error in onMounted:', error);
  }
});

defineExpose({
  dialogRef
});
</script>
<style scoped>
/* Oculta la barra de scroll manteniendo el desplazamiento funcional */
.no-scrollbar {
  -ms-overflow-style: none; /* IE y Edge */
  scrollbar-width: none; /* Firefox */
}
.no-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
